import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";

const TypeWriter = ({ words }) => {
  const [text, setText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const period = 2000;
  const [delta, setDelta] = useState(100);

  useEffect(() => {
    let ticker = setInterval(() => {
      tick();
    }, delta);

    return () => clearInterval(ticker);
  }, [text]);

  const tick = () => {
    let i = loopNum % words.length;
    let fullText = words[i];
    let updatedText = isDeleting
      ? fullText.substring(0, text.length - 1)
      : fullText.substring(0, text.length + 1);

    setText(updatedText);

    if (isDeleting) {
      setDelta((prevDelta) => prevDelta / 2);
    }

    if (!isDeleting && updatedText === fullText) {
      setIsDeleting(true);
      setDelta(period);
    } else if (isDeleting && updatedText === "") {
      setIsDeleting(false);
      setLoopNum(loopNum + 1);
      setDelta(100);
    }
  };

  return <span>{text}</span>;
};

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const { lang, t } = useLanguage();

  const slides = [
    {
      videoUrl: "/videos/hero_1.mp4",
    },
    {
      videoUrl: "/videos/hero_2.mp4",
    },
    {
      videoUrl: "/videos/hero_3.mp4",
    },
    {
      videoUrl: "/videos/hero_4.mp4",
    },
  ];

  // Each video plays in full, then the next one starts
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  useEffect(() => {
    const videoContainer = document.querySelector(".video-container");

    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    if (videoContainer) {
      videoContainer.addEventListener("touchstart", preventDefault, {
        passive: false,
      });
      videoContainer.addEventListener("touchmove", preventDefault, {
        passive: false,
      });
      videoContainer.addEventListener("touchend", preventDefault, {
        passive: false,
      });
      videoContainer.addEventListener("click", preventDefault);
    }

    return () => {
      if (videoContainer) {
        videoContainer.removeEventListener("touchstart", preventDefault);
        videoContainer.removeEventListener("touchmove", preventDefault);
        videoContainer.removeEventListener("touchend", preventDefault);
        videoContainer.removeEventListener("click", preventDefault);
      }
    };
  }, []);

  return (
    <div className="relative w-full overflow-hidden">
      {/* mt-16 clears the fixed navbar */}
      <div className="relative mt-16 w-full aspect-[4/3] md:aspect-auto md:h-[calc(100vh-4rem)]">
        <div className="video-container absolute inset-0 bg-black/50">
          <div
            className="absolute inset-0 z-0 pointer-events-none select-none touch-none"
            style={{
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              KhtmlUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
              userSelect: "none",
            }}
          >
            <video
              key={slides[currentSlide].videoUrl}
              className="h-full w-full object-cover object-top"
              autoPlay
              muted
              onEnded={nextSlide}
              onError={nextSlide}
              playsInline
              controls={false}
              playsinline="true"
              webkit-playsinline="true"
              controlsList="nodownload nofullscreen noremoteplayback"
              disablePictureInPicture
              style={{
                pointerEvents: "none",
                touchAction: "none",
              }}
            >
              <source src={slides[currentSlide].videoUrl} type="video/mp4" />
              {t.common.videoUnsupported}
            </video>
          </div>
        </div>

        {/* On mobile the text sits on a soft gradient over the video instead of boxed labels */}
        <div className="relative h-full flex items-end justify-end text-white bg-gradient-to-t from-black/75 via-black/25 to-transparent md:bg-none [text-shadow:0_1px_4px_rgba(0,0,0,0.8)] md:[text-shadow:none] px-4 md:px-16 pb-4 md:pb-14 pointer-events-auto">
          <div className="text-right">
            <div className="flex flex-col items-end md:gap-2">
              <h1 className="text-xl md:text-5xl lg:text-6xl 2xl:text-7xl md:font-light">
                <span className="inline-block md:backdrop-blur-sm md:bg-gray-900/30 md:px-5 md:py-2 rounded-lg">
                  {t.hero.heading}
                </span>
              </h1>
              <h2 className="text-xl md:text-5xl lg:text-6xl 2xl:text-7xl md:font-light">
                <span className="inline-block md:backdrop-blur-sm md:bg-gray-900/30 md:px-5 md:py-2 rounded-lg">
                  <TypeWriter key={lang} words={t.hero.words} />
                </span>
              </h2>
            </div>
            <button
              onClick={() => navigate("/contact")}
              className="bg-red-400 text-white px-5 md:px-10 py-2 md:py-4 rounded-lg text-base md:text-2xl hover:bg-red-500 transition-colors inline-block mt-3 md:mt-5 md:font-light [text-shadow:none]"
            >
              {t.common.bookAppointment}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
